/**
 * 작성자 : 한영광
 * 날짜 : 2023.07.03
 * 기능 : 설정관리-코드관리 페이지
 */
/**
 * 수정자 : 박승호
 * 날짜 : 2024.10.14
 * 수정 이유 : InfoTextWithIcon => TextInfoBox 컴포넌트로 변경
 */

import React, { useState, useEffect, SetStateAction, Dispatch, useRef } from 'react';
import { v1 } from 'uuid';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { useOutletContext } from 'react-router-dom';

import { userState } from '../atoms';
import styled from 'styled-components';
import { InputTable } from '../assets/styles/InputTable';
import { IModal } from 'customTypes';
import { FLAG_CREATE_OR_UPDATE, FLAG_DELETE } from '../_constants';
import Portal from './Portal';
import DeleteModal from './Modal/DeleteModal';
import ConfirmDeleteModal from './Modal/DeleteModal3';
import { BtnBlue, BtnRed } from './Button';
import { trimArray } from '../utils/trimArray';
import { scrollToNodeBottom } from '../utils/scrollToNodeBottom';
import { logPost } from '../services/log';
import { applyBorderStyle } from '../utils/applyBorderStyle';
import { useSetAuth } from '../utils/useSetAuth';
import illustrator from '../assets/images/illustration/78.svg';
import { scrollToNodeTop } from '../utils/scrollToNodeTop';
import { useTranslation } from 'react-i18next';
import BackButton from './BackButton';
import { apiGet, apiPost } from '../services/_common';
import { useQuery } from '@tanstack/react-query';
import { PulseLoader } from 'react-spinners';
import TextInfoBox from './TextInfoBox';

const EmptyData = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  user-select: none;
  height: 100%;
  width: 100%;
  padding-bottom: 4rem !important;
  img {
    width: 16rem;
    /* user-drag: none; */
    -webkit-user-drag: none;
  }
  p {
    color: var(--filled-violet);
    word-break: keep-all;
    text-align: center;
  }
  p:nth-child(2) {
    padding: 1rem;
    background-color: var(--tonal);
    border-radius: 0.5rem;
    margin: 1rem;
  }
  p:nth-child(3) {
    color: var(--text-tertiary);
    margin: 0 2rem;
  }
`;

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.5rem;
  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;
const Root = styled.div`
  height: initial;
  @media (min-width: 1025px) {
    height: 100%;
  }
  > div:nth-child(2) {
    flex-grow: 1;
    overflow: hidden;
    width: 100%;
    overflow: auto;
    position: relative;
  }
  .inputFormsWrapper {
    border-bottom: 1px solid var(--outline);
    padding: 0.5rem;
    width: 100%;
    flex-grow: 0;
    flex-shrink: 0;
  }
  .inputFormsWrapper.smallTab {
    flex-direction: row;
    padding: 0 0.5rem;
    font-weight: 500;
    width: 100%;
    overflow: auto;
    gap: 0;
    &::-webkit-scrollbar {
      -webkit-appearance: none;
      height: 0rem !important;
    }
    .tab {
      color: var(--text-secondary);
      display: flex;
      align-items: center;
      padding: 0 0.5rem;
      cursor: pointer;
      height: 3.5rem;
      border-bottom: 2px solid transparent;
      flex-shrink: 0;
    }
    .activeTab {
      font-weight: 700;
      color: var(--selected-primary);
      border-bottom: 2px solid var(--selected-primary);
    }
  }
  .thead {
    position: sticky;
    top: 0;
    z-index: 3;
  }
  .inputFormsWrapper {
    border-bottom: 1px solid var(--outline);
    padding: 0.5rem;
    width: 100%;
    flex-grow: 0;
    flex-shrink: 0;
    flex-direction: row;
    .table {
      height: fit-content;
    }
  }
  .formTitle {
    padding: 0 0.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    font-weight: 500;
    color: var(--text-primary);
  }
  .titleDetail {
    color: var(--text-secondary);
    span {
      color: var(--text-primary);
      /* font-weight: 600; */
      margin: 0 0.25rem;
    }
  }
  .thead > .tr {
    > div {
      display: flex;
      align-items: center;
      padding: 0 0.5rem;
    }
  }
  .tr {
    width: fit-content;
    min-width: 100%;
    > div {
      padding: 0 0.5rem;
      flex-shrink: 0;
      > div {
        width: 100%;
        > div > ul {
          display: flex;
          justify-content: center;
          > li {
            width: 100% !important;
            height: 2.25rem !important;
            span {
              width: 100%;
            }
          }
        }
      }
      button {
        width: 3.5rem;
        padding: 0;
        /* padding: 0 0.5rem; */
        font-size: 0.875rem;
      }
    }
    > div:nth-child(1),
    > div:nth-child(3) {
      z-index: 2;
    }
    input[type='text'] {
      height: 2rem;
      font-size: 0.875rem;
    }
  }
  .tbody {
    > div {
      background-color: var(--board);
    }
  }
  .grid {
    display: grid;
    margin-bottom: 1rem;
    align-items: center;
    min-height: 3.4rem;
  }
  .material-symbols-rounded {
    font-variation-settings: 'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 48;
  }
  .arrow-icon span {
    height: 2.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .icon-btn {
    width: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    gap: 0.25rem;
    cursor: pointer;
    border-radius: 5rem;
    padding-right: 1rem;
    padding-left: 0.5rem;
    font-size: 0.875rem;
    background-color: var(--tonal);
    color: var(--text-primary);
  }
  .icon-btn:hover {
    background-color: var(--tonal-deep);
  }
`;

interface INormalCodeSetting {
  type: 'h' | 's' | 'c';
}

interface IComCdList {
  index: number;
  no?: number;
  grCd: string;
  subCd: string;
  cdName: string;
  flag: string;
  writer: string;
  wDate?: string;
  editor?: string;
  eDate?: string;
}

const NormalCodeSetting = ({ type }: INormalCodeSetting) => {
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const size = useOutletContext<any>();
  const userInfo = useRecoilValue(userState);
  const { hCd, sCd } = userInfo;
  const scrollContainerRef = useRef<HTMLInputElement>(null);
  const scrollSubContainerRef = useRef<HTMLInputElement>(null);
  const [tableState, setTableState] = useState<IComCdList[]>([]);
  const [rawData, setRawData] = useState<IComCdList[]>([]);
  const [showSubTable, setShowSubTable] = useState({ status: false, subCd: '', cdName: '' });
  const [subTableState, setSubTableState] = useState<any[]>([]);
  const [subRawData, setSubRawData] = useState<any[]>([]);
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });
  const [newRowIndex, setNewRowIndex] = useState<number>(0); // 신규항목 추가 클릭했을 때 새 로우객체에 인덱스추가
  const [newSubRowIndex, setNewSubRowIndex] = useState<number>(0); // 신규항목 추가 클릭했을 때 새 로우객체에 인덱스추가
  const [rawDataLength, setRawDataLength] = useState<number>(0); // 테이블에 표시할 로우 넘버
  const [subRawDataLength, setSubRawDataLength] = useState<number>(0); // 테이블에 표시할 로우 넘버
  const [isTableInitialRender, setIsTableInitialRender] = useState(true); // 컴포넌트의 이니셜렌더 여부
  const [isSubTableInitialRender, setIsSubTableInitialRender] = useState(true); // 서브테이블 컴포넌트의 이니셜렌더 여부
  const SUB_TABLE_EXIST = '2'; // etc1 값이 2일때, 서브테이블 있음
  const [isSaveClicked, setIsSaveClicked] = useState(false); // 저장1버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const [isSave2Clicked, setIsSave2Clicked] = useState(false); // 저장2버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값

  const {
    data: normalCd,
    isLoading: isLoadingNormalCd,
    isFetching: isFetchingNormalCd,
  } = useQuery([`normalCdGet_${hCd}_${sCd}`, hCd, sCd], () => fetchNormalCd(), {
    enabled: type === 'h' || type === 'c' ? !!hCd && (sCd === '' || sCd === '00000') : !!hCd && !!sCd && sCd !== '00000',
  });

  const [normalCdTitle, setNormalCdTitle] = useState<any>(normalCd ? normalCd[0] : { subCd: '', cdName: '', etc1: '' });

  const fetchNormalCd = async () => {
    try {
      logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '코드 관리 > 일반코드 관리',
        action: '조회',
        etc: ``,
      });
      const res = await apiGet({ path: '/code/normal' });
      if (type === 'h') {
        const newArray = res.data.data.comCdList.filter((el: any) => el.etc2 === '1' || el.etc2 === '3');
        const { subCd, cdName, etc1 } = newArray[0];
        getNormalCdTitleAPI(subCd);
        setNormalCdTitle({ subCd, cdName, etc1 });
        return newArray;
      }
      if (type === 's') {
        const newArray = res.data.data.comCdList.filter((el: any) => el.etc2 === '2' || el.etc2 === '3');
        const { subCd, cdName, etc1 } = newArray[0];
        getNormalCdTitleAPI(subCd);
        setNormalCdTitle({ subCd, cdName, etc1 });
        return newArray;
      }
      {
        const newArray = res.data.data.comCdList.filter((el: any) => el.etc3 === '4');
        const { subCd, cdName, etc1 } = newArray[0];
        getNormalCdTitleAPI(subCd);
        setNormalCdTitle({ subCd, cdName, etc1 });
        return newArray;
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  useEffect(() => {
    //  로우가 1개 이상이고, 이니셜렌더가 아닌경우 스크롤을 하단으로 이동
    if (tableState.length > 0 && !isTableInitialRender) scrollToNodeBottom(scrollContainerRef);
  }, [tableState.length]);

  useEffect(() => {
    if (subTableState.length > 0 && !isSubTableInitialRender) scrollToNodeBottom(scrollSubContainerRef);
  }, [subTableState.length]);

  // 첫번째 테이블 리스트 요청
  const getNormalCdTitleAPI = async (subCd: string) => {
    const req = {
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      subCd,
    };
    const res = await apiGet({ path: '/code/pmsNormalTitle', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const copyArray = [...data.pmsNormalTitleList];
      // Add an index property to each object in the array
      copyArray.forEach((obj: IComCdList, index: number) => {
        obj.no = index + 1; // Adding 1 to start index from 1
      });
      setTableState(copyArray);
      setRawData(data.pmsNormalTitleList);
      setNewRowIndex(data.pmsNormalTitleList.length);
      setRawDataLength(data.pmsNormalTitleList.length);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 현장일반코드 버튼 클릭시, 첫번째테이블 리스트 요청
  // 공종, 직종버튼 클릭시 서브테이블 상세항목(두번째테이블) view status true
  const onClickNormalCdBtn = (el: any) => {
    const { subCd, cdName } = el;
    getNormalCdTitleAPI(el.subCd);
    setNormalCdTitle((prev: any) => ({ ...prev, subCd, cdName, etc1: el.etc1 }));
    if (el.etc1 !== SUB_TABLE_EXIST) setShowSubTable((prev) => ({ ...prev, status: false }));
    setIsTableInitialRender(true);
    setIsSubTableInitialRender(true);
    scrollToNodeTop(scrollContainerRef);
    scrollToNodeTop(scrollSubContainerRef);
  };

  const backToMain = () => {
    setShowSubTable((prev) => ({ ...prev, status: false }));
  };

  const increaseArrayNo = (data: any, state: IComCdList[], setState: Dispatch<SetStateAction<IComCdList[]>>, newIndex: number) => {
    const copyArray2 = [...state, data];
    copyArray2.map((el, i) => {
      if (el.index !== undefined) {
        const copyArray = [...copyArray2];
        copyArray[i] = {
          ...copyArray[i],
          index: newIndex,
          grCd: '000',
          subCd: normalCdTitle.subCd,
          cdName: '',
          flag: FLAG_CREATE_OR_UPDATE,
          writer: userInfo.userId,
          no: copyArray2.length,
        };
        setState(copyArray);
      }
    });
  };

  // 로우 추가 클릭
  const onClickAddTableRow = () => {
    if (normalCdTitle.subCd !== '') {
      setNewRowIndex((prev) => prev + 1);

      const data = {
        index: newRowIndex,
        grCd: '000',
        subCd: normalCdTitle.subCd,
        cdName: '',
        flag: FLAG_CREATE_OR_UPDATE,
        writer: userInfo.userId,
      };
      setTableState((prev: IComCdList[]) => [...prev, data]);
      setRawData((prev: IComCdList[]) => [...prev, data]);

      increaseArrayNo(data, tableState, setTableState, newRowIndex);
      setIsTableInitialRender(false);
    }
  };

  // 상세항목 로우 추가 클릭
  const onClickAddSubTableRow = () => {
    if (normalCdTitle.etc1 === SUB_TABLE_EXIST && showSubTable.status) {
      if (showSubTable.subCd.length > 1) {
        setNewSubRowIndex((prev) => prev + 1);

        const data = {
          index: newSubRowIndex,
          subCd: '',
          grCd: showSubTable.subCd,
          cdName: '',
          flag: FLAG_CREATE_OR_UPDATE,
          writer: userInfo.userId,
        };
        setSubTableState((prev: IComCdList[]) => [...prev, data]);
        setSubRawData((prev: IComCdList[]) => [...prev, data]);

        increaseArrayNo(data, subTableState, setSubTableState, newSubRowIndex);
        setIsSubTableInitialRender(false);
      } else toast.warning(t('입력하신 타이틀 저장 후 상세항목을 입력하세요'));
    }
  };

  // 뷰테이블 값이 수정될때
  const onChangeTableState = (e: React.ChangeEvent<HTMLInputElement>, i: number, el: IComCdList) => {
    const { name, value } = e.currentTarget;
    const viewDataArray = [...tableState]; // 뷰테이블 로우 어레이
    const rawDataArray: any = [...rawData]; // 데이터테이블 로우 어레이

    let findIndex;
    if (el.index === undefined) {
      // 새로운 로우가 아닐때, 데이터테이블에서 해당하는 로우의 인덱스값 찾기
      findIndex = rawData.findIndex((rawEl: IComCdList) => rawEl.subCd === el.subCd);
    } else {
      // 새로운 로우일때 인덱스값
      findIndex = el.index;
    }

    // 신규로우 수정하는 경우
    viewDataArray[i] = { ...viewDataArray[i], [name]: value, flag: FLAG_CREATE_OR_UPDATE }; // 뷰테이블 로우 업데이트
    if (findIndex !== -1) {
      // 데이터테이블 로우 업데이트
      rawDataArray[findIndex] = { ...rawDataArray[findIndex], [name]: value, flag: FLAG_CREATE_OR_UPDATE, editor: userInfo.userId };
    }

    setTableState(viewDataArray);
    setRawData(rawDataArray);
  };

  // 서브 뷰테이블 값이 수정될때
  const onChangeSubTableState = (e: React.ChangeEvent<HTMLInputElement>, i: number, el: any) => {
    const { name, value } = e.currentTarget;
    const viewDataArray: any = [...subTableState]; // 뷰테이블 로우 어레이
    const rawDataArray: any = [...subRawData]; // 데이터테이블 로우 어레이
    let findIndex;

    if (el.index === undefined) {
      // 새로운 로우가 아닐때, 데이터테이블에서 해당하는 로우의 인덱스값 찾기
      findIndex = subRawData.findIndex((rawEl: IComCdList) => rawEl.subCd === el.subCd); // 데이터테이블에서 해당하는 로우의 인덱스값
    } else {
      findIndex = el.index; // 새로운 로우일때 인덱스값
    }
    // 그룹코드 외 다른항목을 수정한 경우 또는 신규로우의 그룹코드를 수정하는 경우
    viewDataArray[i] = { ...viewDataArray[i], [name]: value, flag: FLAG_CREATE_OR_UPDATE }; // 뷰테이블 로우 업데이트
    setSubTableState(viewDataArray);

    if (findIndex !== -1) {
      // 데이터테이블 로우 업데이트
      rawDataArray[findIndex] = { ...rawDataArray[findIndex], [name]: value, flag: FLAG_CREATE_OR_UPDATE, editor: userInfo.userId };
    }
    setSubRawData(rawDataArray);
  };

  const deleteCodeAPI = async (el: any, confirm?: boolean) => {
    const req = {
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      grCd: el.grCd,
      subCd: el.subCd,
      editor: userInfo.userId,
      confirm,
    };
    const res = await apiPost({ path: '/code/pmsNormalDelete', contentType: 'application/json', req });
    const { message, statusCode, data } = res.data;
    if (statusCode === 200) {
      setShowSubTable({ status: false, subCd: '', cdName: '' });
      setOpenModal((prev) => ({ ...prev, status: false }));
      toast.success(t(`${type === 'c' ? '사내' : '현장'} 관리자용 일반코드 삭제 성공`));

      setTableState(data.pmsNormalList);

      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '코드 관리 > 일반코드 관리',
        action: `${normalCdTitle.cdName} 삭제`,
        etc: `${el.cdName}(${el.subCd})`,
      });
    } else if ((el.grCd === 'N', statusCode === 400)) {
      toast.error(t(message));
    } else if (statusCode === 207) {
      setOpenModal((prev) => ({ ...prev, status: true, type: 'confirmDelete', content: el.subCd.toString().substring(0, 1) === 'A' ? '공종' : '직종', api: () => deleteCodeAPI(el, true) }));
    } else {
      // toast.error(t(ERROR));
    }
  };

  const deletesubCodeAPI = async (el: any, confirm?: boolean) => {
    const req = {
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      grCd: el.grCd,
      subCd: el.subCd,
      editor: userInfo.userId,
      confirm,
    };
    const res = await apiPost({ path: '/code/pmsNormalDelete', contentType: 'application/json', req });
    const { message, statusCode, data } = res.data;
    if (statusCode === 200) {
      setOpenModal((prev) => ({ ...prev, status: false }));
      toast.success(t(message));

      setSubTableState(data.pmsNormalList);

      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '코드 관리 > 일반코드 관리',
        action: `${normalCdTitle.cdName}(상세항목) 삭제`,
        etc: `${el.cdName}(${el.subCd})`,
      });
    } else if (statusCode === 207) {
      setOpenModal((prev) => ({ ...prev, status: true, type: 'confirmDelete', content: '직종', api: () => deletesubCodeAPI(el, true) }));
    } else {
      // toast.error(t(ERROR));
    }
  };

  const decreaseArrayNo = (array: IComCdList[], dataLength: number, setState: Dispatch<SetStateAction<IComCdList[]>>) => {
    let increase = 0;

    const newArray = array.map((el, i) => {
      if (el.index !== undefined) {
        increase += 1;
        const copyArray = [...array];
        copyArray[i] = {
          ...copyArray[i],
          no: dataLength + increase,
        };
        return copyArray[i];
      }
      return el;
    });
    setState(newArray);
  };

  const onClickDelete = (el: IComCdList, i: number) => {
    // 새로 추가한 로우 제거 클릭 시
    if (el.index !== undefined) {
      // 뷰테이블 로우 삭제
      const updatedArray = [...tableState];
      updatedArray.splice(i, 1);

      decreaseArrayNo(updatedArray, rawDataLength, setTableState);

      // 데이터테이블 로우 삭제 (플래그 D로 업데이트)
      const findIndex = rawData.findIndex((rawEl: IComCdList) => rawEl.index === el.index);
      const rawDataArray: any = [...rawData];
      if (findIndex !== -1) {
        rawDataArray[findIndex] = {
          ...rawDataArray[findIndex],
          flag: FLAG_DELETE,
        };
        setRawData(rawDataArray);
      }
    } else {
      // 삭제 시 에디터아이디 추가
      const data = {
        ...el,
        editor: userInfo.userId,
      };
      setOpenModal((prev) => ({
        ...prev,
        status: true,
        type: 'delete',
        state: tableState,
        setState: setTableState,
        api: deleteCodeAPI,
        el: data,
        index: i,
        withoutRowDelete: true,
      }));
    }
    setIsTableInitialRender(true); // 삭제 클릭시 테이블길이 변화인한 스크롤하단 이동방지
  };

  // 서브로우 삭제 클릭
  const onClickSubDelete = (el: any, i: number) => {
    // 새로 추가한 로우 제거 클릭 시
    if (el.index !== undefined) {
      // 뷰테이블 로우 삭제
      const updatedArray = [...subTableState];
      updatedArray.splice(i, 1);

      decreaseArrayNo(updatedArray, subRawDataLength, setSubTableState);

      // setSubTableState(updatedArray);
      // 데이터테이블 로우 삭제 (플래그 D로 업데이트)
      const findIndex = subRawData.findIndex((rawEl: IComCdList) => rawEl.index === el.index);
      const rawDataArray: any = [...subRawData];
      if (findIndex !== -1) {
        rawDataArray[findIndex] = {
          ...rawDataArray[findIndex],
          flag: FLAG_DELETE,
        };
        setSubRawData(rawDataArray);
      }
    } else {
      // 삭제 시 에디터아이디 추가
      const data = {
        ...el,
        editor: userInfo.userId,
      };
      setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', state: subTableState, setState: setSubTableState, api: deletesubCodeAPI, el: data, index: i, withoutRowDelete: true }));
    }
    setIsSubTableInitialRender(true); // 삭제 클릭시 테이블길이 변화인한 스크롤하단 이동방지
  };

  // 공종/직종시 두번째 테이블 리스트 요청
  const getSubTableDataAPI = async (subCd: string) => {
    const req = {
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      grCd: subCd,
    };
    const res = await apiGet({ path: '/code/pmsNormalSub', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const copyArray = [...data.pmsNormalSubList];
      // Add an index property to each object in the array
      copyArray.forEach((obj: IComCdList, index: number) => {
        obj.no = index + 1; // Adding 1 to start index from 1
      });

      setSubRawData(copyArray);
      setSubTableState(data.pmsNormalSubList);
      setNewSubRowIndex(data.pmsNormalSubList.length);
      setSubRawDataLength(data.pmsNormalSubList.length);
      setShowSubTable((prev) => ({ ...prev, status: true }));
    } else {
      // toast.error(t(ERROR));
    }
  };

  const onClickOpenSubTable = (el: any) => {
    // 공종/직종일  때만 서브테이블 오픈
    if (normalCdTitle.etc1 === SUB_TABLE_EXIST) {
      setShowSubTable((prev) => ({ ...prev, subCd: el.subCd, cdName: el.cdName }));
      getSubTableDataAPI(el.subCd);
      scrollToNodeTop(scrollSubContainerRef);
    }
  };

  const saveNormalCdArrayAPI = async (normalCdArray: any) => {
    if (normalCdArray.length > 0) {
      const newNormalCdArray = trimArray(normalCdArray).map((v: any) => {
        return {
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          grCd: v.grCd,
          subCd: v.subCd,
          cdName: v.cdName,
          editor: userInfo.userId,
          flag: v.flag,
        };
      });
      const req = { pmsNormalReqDto: newNormalCdArray };
      const res = await apiPost({ path: '/code/pmsNormal', req });
      const { statusCode, data } = res.data;
      if (statusCode === 200) {
        toast.success(t(`${type === 'c' ? '사내' : '현장'} 관리자용 일반코드 신규 & 수정 성공`));
        const copyArray = [...data.pmsNormalTitleList];
        // Add an index property to each object in the array
        copyArray.forEach((obj: IComCdList, index: number) => {
          obj.no = index + 1; // Adding 1 to start index from 1
        });

        setTableState(copyArray);
        setRawData(data.pmsNormalTitleList);
        setNewRowIndex(data.pmsNormalTitleList.length);

        const findIndex = normalCdArray.findIndex((el: any) => el.subCd === showSubTable.subCd);
        if (findIndex !== -1) {
          setShowSubTable((prev) => ({ ...prev, subCd: normalCdArray[findIndex].subCd, cdName: normalCdArray[findIndex].cdName })); //  저장 성공시 마지막클릭한 로우 기준으로 코드항목테이블 항목명변경
        }
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '코드 관리 > 일반코드 관리',
          action: `${normalCdTitle.cdName} 저장`,
          etc: `신규 ${newNormalCdArray.filter((v: any) => v.subCd.length === 1).length}건, 수정 ${newNormalCdArray.filter((v: any) => v.subCd.length !== 1).length}건`,
        });
      } else {
        // toast.error(t(ERROR));
      }
    } else {
      toast.warning(t('변경된 내용이 없습니다'));
    }
  };

  const saveSubNormalCdArrayAPI = async (normalCdArray: any) => {
    if (normalCdArray.length > 0) {
      const newNormalCdArray = trimArray(normalCdArray).map((v: any) => {
        return {
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          grCd: v.grCd,
          subCd: v.subCd,
          cdName: v.cdName,
          editor: userInfo.userId,
          flag: v.flag,
        };
      });
      const req = { pmsNormalSubReqDto: newNormalCdArray };
      const res = await apiPost({ path: '/code/pmsNormalSub', req });
      // const res = await pmsNormalSubPost(reqData);
      const { message, statusCode, data } = res.data;
      if (statusCode === 200) {
        toast.success(t(message));

        const copyArray = [...data.pmsNormalSubList];
        // Add an index property to each object in the array
        copyArray.forEach((obj: IComCdList, index: number) => {
          obj.no = index + 1; // Adding 1 to start index from 1
        });

        setSubTableState(copyArray);
        setSubRawData(data.pmsNormalSubList);
        setNewRowIndex(data.pmsNormalSubList.length);
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '코드 관리 > 일반코드 관리',
          action: `${normalCdTitle.cdName}(상세항목) 저장`,
          etc: `신규 ${newNormalCdArray.filter((v: any) => v.subCd === '').length}건, 수정 ${newNormalCdArray.filter((v: any) => v.subCd !== '').length}건`,
        });
      } else {
        // toast.error(t(ERROR));
      }
    } else {
      toast.warning(t('변경된 내용이 없습니다'));
    }
  };

  const onClickSaveTableState = () => {
    setIsSaveClicked(true);
    if (normalCdTitle.subCd !== '') {
      const newRowRequired = ['cdName'];

      const emptyCheck = rawData.filter((el: any) => {
        const check = newRowRequired.find((el2: any) => el[el2] === '' && el.flag === FLAG_CREATE_OR_UPDATE);
        return check;
      });
      if (emptyCheck.length > 0) {
        toast.warning(t('필수입력값을 모두 입력하세요'));
      } else {
        // 빈값이 없는 경우 수정,업데이트된 로우들만 서버에 전달
        const dbSendRows = rawData.filter((el: any) => el.flag === FLAG_CREATE_OR_UPDATE);
        const newArray = dbSendRows.map(({ index, no, wDate, eDate, ...rest }) => rest);

        saveNormalCdArrayAPI(newArray);
      }
    }
  };

  const onClickSaveSubTableState = () => {
    setIsSave2Clicked(true);
    if (subRawData.length > 0) {
      const newRowRequired = ['cdName'];
      const emptyCheck = subRawData.filter((el: any) => {
        const check = newRowRequired.find((el2) => !el[el2] && el.flag === FLAG_CREATE_OR_UPDATE);
        return check;
      });
      if (subRawData[0].grCd.length === 1) {
        toast.warning(t('입력하신 타이틀 저장 후 상세항목을 입력하세요'));
      } else if (emptyCheck.length > 0) {
        toast.warning(t('필수입력값을 모두 입력하세요'));
      } else {
        const dbSendRows = subRawData.filter((el) => el.flag === FLAG_CREATE_OR_UPDATE);
        const newArray = dbSendRows.map(({ index, wDate, eDate, no, ...rest }) => rest);

        saveSubNormalCdArrayAPI(newArray);
      }
    }
  };

  const renderCreateBtn = (info: string) => {
    if (auth.createAuth) {
      return <BtnBlue onClick={info === 'main' ? onClickAddTableRow : onClickAddSubTableRow}>{t('신규항목 추가')}</BtnBlue>;
    }
    return null;
  };

  const renderSaveBtn = (info: string) => {
    if (auth.updateAuth) {
      return <BtnBlue onClick={info === 'main' ? onClickSaveTableState : onClickSaveSubTableState}>{t('저장')}</BtnBlue>;
    }
    return null;
  };

  const renderDeleteBtn = (el: any, i: number, info: string) => {
    if (auth.deleteAuth) {
      return <BtnRed onClick={() => (info === 'main' ? onClickDelete(el, i) : onClickSubDelete(el, i))}>{el.index === undefined ? t('삭제') : t('제거')}</BtnRed>;
    }
    if (el.index !== undefined && (auth.createAuth || auth.updateAuth)) {
      return <BtnRed onClick={() => (info === 'main' ? onClickDelete(el, i) : onClickSubDelete(el, i))}>{t('제거')}</BtnRed>;
    }
    return null;
  };

  if (isLoadingNormalCd || isFetchingNormalCd)
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );

  return (
    <div className={`${showSubTable.status ? 'content-container twoColumn column-55' : 'content-container oneColumn'}`}>
      {(size.innerSize.W > 1024 || (size.innerSize.W <= 1024 && !showSubTable.status)) && (
        <Root>
          <div className='inputFormsWrapper smallTab'>
            {normalCd?.map((el: IComCdList) => (
              <div key={v1()} className={`tab ${normalCdTitle.subCd === el.subCd ? 'activeTab' : undefined}`} role='button' tabIndex={0} onClick={() => onClickNormalCdBtn(el)}>
                {t(el.cdName)}
              </div>
            ))}
          </div>
          <InputTable className='margin-left-05'>
            <div ref={scrollContainerRef}>
              <div className='thead'>
                <div className='tr'>
                  <div className='trCol2p5 flex-center tableStickyNo'>{t('No')}</div>
                  <div className='trCol3 flex-center'>{t('코드')}</div>
                  <div className='trCol12 flex-center tableStickyTitle content-overflow required'>{t('항목 명')}</div>
                  <div className='trCol12 flex-center '>{t('수정일자')}</div>
                  <div className='trCol6 flex-center'>{t('수정자')}</div>
                  <div className='trCol6' />
                </div>
              </div>
              {normalCdTitle.subCd === 'E' && <TextInfoBox ico='bolt' description='한국의 경우 별도 등록 필요 없음' color='orange' />}
              <div className='tbody'>
                {tableState?.length > 0 ? (
                  tableState?.map((el, i) => (
                    <div className='tr' key={`sub${el.subCd}_${i}`} role='button' tabIndex={0} onClick={() => onClickOpenSubTable(el)}>
                      <div className='trCol2p5 flex-center tableStickyNo'>{el.no}</div>
                      <div className='trCol3 flex-center'>{el.index === undefined ? el.subCd : ''}</div>
                      <div className='trCol12 flex-center tableStickyTitle content-overflow'>
                        <input
                          type='text'
                          value={el.cdName}
                          id='cdName'
                          name='cdName'
                          onChange={(e) => onChangeTableState(e, i, el)}
                          style={isSaveClicked ? applyBorderStyle(el.cdName, 'red', 'cdName') : undefined}
                          onClick={(event) => {
                            if (size.innerSize.W <= 1024) {
                              event.stopPropagation();
                            }
                          }}
                          maxLength={50}
                        />
                      </div>
                      <div className='trCol12 flex-center '>{el.eDate}</div>
                      <div className='trCol6 flex-center'>{el.index === undefined ? el.editor : ''}</div>

                      <div className='trCol6 buttonWrapper flex-center'>{renderDeleteBtn(el, i, 'main')}</div>
                    </div>
                  ))
                ) : (
                  <div className='noData'>No data.</div>
                )}
                {/* </div> */}
              </div>
            </div>
          </InputTable>
          <ButtonsWrapper>
            {renderCreateBtn('main')}
            {renderSaveBtn('main')}
          </ButtonsWrapper>
        </Root>
      )}
      {showSubTable.status && (
        <Root>
          <div className='inputFormsWrapper'>
            {size.innerSize.W <= 1024 && <BackButton func={() => backToMain()} />}
            <div className='formTitle titleDetail'>
              {t('코드')}
              <span>{showSubTable.subCd}</span>• {t('항목 명')} <span> {showSubTable.cdName}</span>
            </div>
          </div>
          {subTableState?.length > 0 ? (
            <InputTable className='margin-left-05'>
              <div ref={scrollSubContainerRef}>
                <div className='thead'>
                  <div className='tr'>
                    <div className='trCol2p5 flex-center tableStickyNo'>No</div>
                    <div className='trCol3 flex-center'>{t('코드')}</div>
                    <div className='trCol12 flex-center content-overflow  tableStickyTitle'>{t('항목 명')}</div>
                    <div className='trCol12 flex-center'>{t('수정일자')}</div>
                    <div className='trCol6 flex-center'>{t('수정자')}</div>
                    <div className='trCol6'> </div>
                  </div>
                </div>
                <div className='tbody'>
                  {subTableState?.map((el, i) => (
                    <div className='tr' key={`sub${el.subCd}_${i}`} role='button' tabIndex={0}>
                      <div className='trCol2p5 flex-center tableStickyNo'>{el.no}</div>
                      <div className='trCol3 flex-center'>{el.index === undefined ? el.subCd : ''}</div>
                      <div className='trCol12 flex-center content-overflow tableStickyTitle'>
                        <input
                          type='text'
                          value={el.cdName}
                          id='cdName'
                          name='cdName'
                          onChange={(e) => onChangeSubTableState(e, i, el)}
                          style={isSave2Clicked ? applyBorderStyle(el.cdName, 'red', 'cdName') : undefined}
                          maxLength={50}
                        />
                      </div>
                      <div className='trCol12 flex-center'>{el.eDate}</div>
                      <div className='trCol6 flex-center'>{el.index === undefined ? el.editor : ''}</div>
                      <div className='trCol6 flex-center buttonWrapper'>{renderDeleteBtn(el, i, 'sub')}</div>
                    </div>
                  ))}
                </div>
              </div>
            </InputTable>
          ) : (
            <EmptyData>
              <img src={illustrator} alt='noData' />
              <p>{t('상세 항목이 존재하지 않습니다')}</p>
              <p>{t('신규 항목을 추가하시고 저장해 주세요')}</p>
            </EmptyData>
          )}
          <ButtonsWrapper>
            {renderCreateBtn('sub')}
            {renderSaveBtn('sub')}
          </ButtonsWrapper>
        </Root>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.type === 'delete' && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}
        {openModal.type === 'confirmDelete' && <ConfirmDeleteModal openModal={openModal} setOpenModal={setOpenModal} />}
      </Portal>
    </div>
  );
};

export default NormalCodeSetting;
